import React, { useEffect, useRef, useState } from "react";
import { X } from "@phosphor-icons/react";
import { Button } from "../ui/button";
import { Dialog, DialogContent } from "../ui/dialog";
import InternalNotesFloatingPanel from "./InternalNotesFloatingPanel";
import QuotationPreviewToolbar from "./QuotationPreviewToolbar";
import QuotationPrintView from "./QuotationPrintView";

export function QuotationPreviewModal({
  quotation,
  open,
  onOpenChange,
  t,
  companyInfo,
  clientName,
  statusLabel,
  internalNotesOpen,
  onToggleInternalNotes,
  onClose,
  onPrint,
  onExportPdf,
  onEdit,
  onMarkSent,
  onAccept,
  onReject,
  onConvertToInvoice,
}) {
  const previewViewportRef = useRef(null);
  const [previewScale, setPreviewScale] = useState(1);

  useEffect(() => {
    if (!quotation) return undefined;

    const updatePreviewScale = () => {
      const viewport = previewViewportRef.current;
      const visualViewport = window.visualViewport;
      const isSmallScreen = window.matchMedia("(max-width: 640px)").matches;
      const availableWidth = isSmallScreen
        ? Math.max((visualViewport?.width || window.innerWidth) - 72, 240)
        : Math.max((viewport?.clientWidth || 794) - 32, 280);
      const availableHeight = isSmallScreen
        ? Math.max((visualViewport?.height || window.innerHeight) - 176, 300)
        : Math.max((viewport?.clientHeight || 1123) - 32, 320);
      const safeScale = Math.min(1, availableWidth / 794, availableHeight / 1123);
      setPreviewScale(isSmallScreen ? safeScale * 0.96 : safeScale);
    };

    updatePreviewScale();
    const resizeObserver = new ResizeObserver(updatePreviewScale);
    if (previewViewportRef.current) {
      resizeObserver.observe(previewViewportRef.current);
    }
    window.visualViewport?.addEventListener("resize", updatePreviewScale);
    window.addEventListener("resize", updatePreviewScale);

    return () => {
      resizeObserver.disconnect();
      window.visualViewport?.removeEventListener("resize", updatePreviewScale);
      window.removeEventListener("resize", updatePreviewScale);
    };
  }, [quotation]);

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="left-[50%] top-2 h-[calc(100svh-16px)] max-h-none w-[calc(100vw-16px)] max-w-none translate-x-[-50%] translate-y-0 overflow-hidden rounded-lg border bg-[#E5E7EB] p-0 text-[#0B0E14] sm:top-[50%] sm:h-[92vh] sm:max-h-[92vh] sm:w-[96vw] sm:max-w-[1040px] sm:translate-y-[-50%] print:max-w-none print:shadow-none">
          {quotation && (
            <div className="flex h-full flex-col print:block print:p-8">
              <Button
                type="button"
                variant="outline"
                aria-label="Cerrar vista de cotización"
                onClick={onClose}
                className="print:hidden absolute right-3 top-3 z-30 h-10 w-10 border-gray-300 bg-white p-0 text-gray-700 shadow-sm hover:bg-gray-50"
              >
                <X className="h-4 w-4" />
              </Button>

              <QuotationPreviewToolbar
                quotation={quotation}
                t={t}
                onPrint={onPrint}
                onExportPdf={onExportPdf}
                onEdit={onEdit}
                onMarkSent={onMarkSent}
                onAccept={onAccept}
                onReject={onReject}
                onConvertToInvoice={onConvertToInvoice}
              />

              <div
                ref={previewViewportRef}
                className="relative flex-1 overflow-auto print:overflow-visible"
              >
                <InternalNotesFloatingPanel
                  notes={quotation.internal_notes}
                  open={internalNotesOpen}
                  onToggle={onToggleInternalNotes}
                />

                <div className="flex min-h-full items-center justify-center p-3 sm:p-4 print:block print:p-0">
                  <div className="w-full min-w-0 max-w-[794px]">
                    <div
                      id="quotation-print"
                      className="mx-auto shadow-2xl print:shadow-none"
                      style={{
                        width: `${794 * previewScale}px`,
                        minHeight: `${1123 * previewScale}px`,
                      }}
                    >
                      <div
                        className="quotation-preview-scale"
                        style={{
                          width: "794px",
                          transform: `scale(${previewScale})`,
                          transformOrigin: "top left",
                        }}
                      >
                        <QuotationPrintView
                          quotation={quotation}
                          companyInfo={companyInfo}
                          clientName={clientName}
                          statusLabel={statusLabel}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {quotation && (
        <div
          style={{
            position: "fixed",
            left: "-10000px",
            top: 0,
            width: "794px",
            pointerEvents: "none",
            visibility: "hidden",
          }}
        >
          <div id="quotation-pdf" style={{ visibility: "visible" }}>
            <QuotationPrintView
              quotation={quotation}
              companyInfo={companyInfo}
              clientName={clientName}
              statusLabel={statusLabel}
            />
          </div>
        </div>
      )}
    </>
  );
}

export default QuotationPreviewModal;
